/**
 * <progress-bar> web component
 *
 * Manages a cart-goal progress bar whose initial HTML is fully server-rendered
 * by sections/progress-bar.liquid. This component's only responsibilities are:
 *   1. Parse static offer data from the hidden <div class="progress-bar__offers-source">
 *      child elements (data attributes on each .progress-bar__offer-item).
 *   2. Perform an initial sync pass so that cloned instances reflect the live
 *      cart total at the time they are appended to the DOM.
 *   3. Subscribe to cart update events and incrementally update the fill width,
 *      header text, and icon states — never replacing the full innerHTML.
 *
 * Dependencies:
 *   - formatMoney()       defined in assets/custom.js (loaded before this file)
 *   - window.moneyFormat  set in layout/theme.liquid from {{ shop.money_format | json }}
 *   - window.MinimogEvents  theme event bus (assets/app.js)
 *       Listened event: 'ON_CART_UPDATE' — payload is the Shopify cart object.
 *       cart.total_price is in cents.
 *
 * Data attributes (set by the Liquid section, survive cloneNode):
 *   data-cart-total        Cart total in cents at page render time
 *   data-show-icons        "true" | "false"
 *   data-success-message   Text displayed when all offers are reached
 */
class ProgressBar extends HTMLElement {
  #cartTotal = 0;
  #offers = [];
  #showIcons = true;
  #successMessage = '';
  #tickIconHtml = '';
  #cartUpdateHandler = null;

  connectedCallback() {
    this.#readAttributes();
    this.#parseOffersFromChildren();
    this.#readTickIconFromTemplate();

    this.#updateProgress();
    this.#listenForCartUpdates();
  }

  disconnectedCallback() {
    if (this.#cartUpdateHandler && window.MinimogEvents?.unSubscribe) {
      window.MinimogEvents.unSubscribe('ON_CART_UPDATE', this.#cartUpdateHandler);
    }
  }

  #readAttributes() {
    this.#cartTotal = parseInt(this.dataset.cartTotal, 10) || 0;
    this.#showIcons = this.dataset.showIcons !== 'false';
    this.#successMessage = this.dataset.successMessage || '';
  }

  #parseOffersFromChildren() {
    const offerElements = this.querySelectorAll(
      '.progress-bar__offers-source .progress-bar__offer-item'
    );

    this.#offers = Array.from(offerElements)
      .map((element) => ({
        label: element.dataset.label || '',
        targetValue: parseInt(element.dataset.targetValue, 10) || 0,
        iconUrl: element.dataset.iconUrl || null,
      }))
      .sort((offerA, offerB) => offerA.targetValue - offerB.targetValue);
  }

  #readTickIconFromTemplate() {
    if (this.#tickIconHtml) return;

    const tickTemplate = this.querySelector(
      '.progress-bar__offers-source template[data-tick-icon]'
    );
    if (tickTemplate) {
      this.#tickIconHtml = tickTemplate.innerHTML.trim();
    }
  }

  #getMaxTargetValue() {
    return this.#offers.at(-1)?.targetValue ?? 0;
  }

  #getNextUnreachedOffer() {
    return this.#offers.find((offer) => this.#cartTotal < offer.targetValue) ?? null;
  }

  #getProgressPercentage() {
    const maxTargetValue = this.#getMaxTargetValue();
    if (maxTargetValue === 0) return 0;
    return Math.min((this.#cartTotal / maxTargetValue) * 100, 100);
  }

  #updateProgress() {
    this.#updateFill();
    this.#updateHeaderText();

    if (this.#showIcons) {
      this.#updateIconStates();
    }
  }

  #updateFill() {
    const progressPercentage = this.#getProgressPercentage();

    const fillElement = this.querySelector('.progress-bar__fill');
    if (fillElement) {
      fillElement.style.width = `${progressPercentage}%`;
    }

    const trackElement = this.querySelector('.progress-bar__track');
    if (trackElement) {
      trackElement.setAttribute('aria-valuenow', Math.round(progressPercentage));
    }
  }

  #updateHeaderText() {
    const offerLabelElement = this.querySelector('.progress-bar__offer-label');
    const distanceTextElement = this.querySelector('.progress-bar__distance-text');
    const nextOffer = this.#getNextUnreachedOffer();

    if (!nextOffer) {
      if (offerLabelElement) offerLabelElement.textContent = '';
      if (distanceTextElement) {
        distanceTextElement.textContent = this.#successMessage;
        distanceTextElement.classList.add('progress-bar__success-message');
      }
      return;
    }

    if (distanceTextElement) {
      distanceTextElement.classList.remove('progress-bar__success-message');
    }

    const amountAway = nextOffer.targetValue - this.#cartTotal;
    const formattedAmount = formatMoney(amountAway, window.moneyFormat);

    if (offerLabelElement) offerLabelElement.textContent = nextOffer.label;
    if (distanceTextElement) distanceTextElement.textContent = `${formattedAmount} away`;
  }

  #updateIconStates() {
    this.#offers.forEach((offer) => {
      const iconWrapper = this.querySelector(
        `.progress-bar__icon-wrapper[data-target-value="${offer.targetValue}"]`
      );
      if (!iconWrapper) return;

      const isReached = this.#cartTotal >= offer.targetValue;
      const wasAlreadyReached = iconWrapper.classList.contains('progress-bar__icon-wrapper--reached');

      if (isReached && !wasAlreadyReached) {
        iconWrapper.classList.add('progress-bar__icon-wrapper--reached');

        const iconElement = iconWrapper.querySelector('.progress-bar__icon');
        if (iconElement) {
          iconElement.classList.add('progress-bar__icon--reached');
        }

        const badge = document.createElement('span');
        badge.className = 'progress-bar__icon-badge';
        badge.setAttribute('aria-hidden', 'true');
        badge.innerHTML = this.#tickIconHtml;
        iconWrapper.appendChild(badge);
      } else if (!isReached && wasAlreadyReached) {
        iconWrapper.classList.remove('progress-bar__icon-wrapper--reached');

        const iconElement = iconWrapper.querySelector('.progress-bar__icon');
        if (iconElement) {
          iconElement.classList.remove('progress-bar__icon--reached');
        }

        iconWrapper.querySelector('.progress-bar__icon-badge')?.remove();
      }
    });
  }

  #listenForCartUpdates() {
    if (!window.MinimogEvents?.subscribe || this.#cartUpdateHandler) return;

    this.#cartUpdateHandler = (cart) => {
      this.#cartTotal = cart.total_price;
      this.#updateProgress();
    };

    window.MinimogEvents.subscribe('ON_CART_UPDATE', this.#cartUpdateHandler);
  }
}

customElements.define('progress-bar', ProgressBar);
